import { useCallback, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Text,
  IconButton,
  HStack,
  Badge,
  createStandaloneToast,
} from "@chakra-ui/react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { NAVBAR_HEIGHT } from "components/navbar/NavbarAdmin";
import { Column, Row } from "hooks/useTable";
import GamelightTable from "components/table";
import { useTableContext } from "contexts/TableContext";
import { useConfirmDialog } from "components/confirmDialog";
import {
  hideGlobalLoading,
  showGlobalLoading,
} from "components/loading/GlobalLoading";
import AddExclusionModal, {
  AddExclusionModalRef,
} from "views/admin/gameNetworkExclusions/components/AddExclusionModal";
import { GameNetworkExclusion } from "views/admin/gameNetworkExclusions/types";
import {
  getGameNetworkExclusions,
  deleteGameNetworkExclusion,
} from "views/admin/gameNetworkExclusions/services";

interface CustomColumn extends Omit<Column<GameNetworkExclusion>, "columnKey"> {
  columnKey: string;
}

const GameNetworkExclusionsTable = () => {
  const { refreshData } = useTableContext();
  const modalRef = useRef<AddExclusionModalRef>();

  const getPlatformName = useMemo(
    () =>
      (platformId: number | null): string => {
        if (!platformId) return "-";
        switch (platformId) {
          case 1:
            return "iOS";
          case 2:
            return "Android";
          default:
            return `Platform ${platformId}`;
        }
      },
    []
  );

  const { ConfirmDialog, openDialog } = useConfirmDialog({
    title: "Delete Exclusion",
    description: "Are you sure you want to delete this game network exclusion?",
    destructive: true,
  });

  const handleDeleteExclusion = useCallback(
    async (exclusionId: number) => {
      const result = await openDialog();

      if (result) {
        showGlobalLoading();
        try {
          await deleteGameNetworkExclusion(exclusionId);

          const toast = createStandaloneToast();
          toast({
            title: "Success",
            description: "Game network exclusion deleted successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
          });

          refreshData();
        } catch (error: any) {
          console.error("Error deleting exclusion:", error);
          const toast = createStandaloneToast();
          toast({
            title: "Error",
            description:
              error.message || "Failed to delete game network exclusion",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        } finally {
          hideGlobalLoading();
        }
      } else {
      }
    },
    [openDialog, refreshData]
  );

  const memoizedColumns = useMemo(() => {
    const COLUMNS: CustomColumn[] = [
      {
        columnKey: "companyName",
        header: "COMPANY NAME",
        render: (exclusion: GameNetworkExclusion) => (
          <Text fontWeight="medium">{exclusion.companyName || "-"}</Text>
        ),
      },
      {
        columnKey: "partnerId",
        header: "PARTNER ID",
        render: (exclusion: GameNetworkExclusion) => (
          <Badge
            colorScheme={exclusion.partnerId ? "blue" : "gray"}
            variant="outline"
          >
            {exclusion.partnerId || "-"}
          </Badge>
        ),
      },
      {
        columnKey: "platformId",
        header: "PLATFORM",
        render: (exclusion: GameNetworkExclusion) => (
          <Badge
            colorScheme={
              exclusion.platformId === 1
                ? "gray"
                : exclusion.platformId === 2
                ? "green"
                : "red"
            }
            variant="solid"
          >
            {getPlatformName(exclusion.platformId)}
          </Badge>
        ),
      },
      {
        columnKey: "packageName",
        header: "PACKAGE NAME",
        render: (exclusion: GameNetworkExclusion) => (
          <Text fontFamily="mono" fontSize="sm">
            {exclusion.packageName || "-"}
          </Text>
        ),
      },
      {
        columnKey: "network",
        header: "NETWORK",
        render: (exclusion: GameNetworkExclusion) => (
          <Text fontWeight="medium">{exclusion.network || "-"}</Text>
        ),
      },
      {
        columnKey: "actions",
        header: "ACTIONS",
        render: (exclusion: GameNetworkExclusion) => (
          <HStack spacing={2}>
            <IconButton
              aria-label="Delete exclusion"
              icon={<FaTrash />}
              colorScheme="red"
              variant="outline"
              size="sm"
              onClick={() => handleDeleteExclusion(exclusion.id)}
            />
          </HStack>
        ),
      },
    ];
    return COLUMNS;
  }, [getPlatformName, handleDeleteExclusion]);

  const ROW_CONFIG: Row<GameNetworkExclusion> = {
    onClick(row) {},
    rowStyle(row) {
      return {};
    },
  };

  return (
    <Box flex={1} minH={"90vh"} pt={NAVBAR_HEIGHT} px={4}>
      <GamelightTable
        columns={memoizedColumns as any}
        fetchData={getGameNetworkExclusions}
        uniqueKey="id"
        title="Game Network Exclusions"
        shouldSearch
        searchPlaceHolder="Search exclusions..."
        rowConfig={ROW_CONFIG}
        buttons={[
          <Button
            key="add-exclusion"
            leftIcon={<FaPlus />}
            colorScheme="blue"
            onClick={() => modalRef.current?.openDialog()}
          >
            Add Exclusion
          </Button>,
        ]}
      >
        <AddExclusionModal ref={modalRef} />
      </GamelightTable>

      {ConfirmDialog}
    </Box>
  );
};

export default GameNetworkExclusionsTable;
